// ============================================================
// Minimal S3-compatible client (AWS SigV4, WebCrypto only)
//
// Works against AWS S3, Google Cloud Storage (XML API + HMAC keys) and
// Cloudflare R2 (S3 API). Only what exports need: PUT, DELETE, GET.
// ============================================================
const enc = new TextEncoder();
/**
 * The runtime's fetch, called unbound. workerd throws "Illegal invocation" when the global
 * fetch is stored on an object and called as a method, so never default to `fetch` itself.
 */
export const globalFetch = (input, init) => fetch(input, init);
function hex(buf) {
    return [...(buf instanceof Uint8Array ? buf : new Uint8Array(buf))]
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}
export async function sha256Hex(data) {
    const bytes = typeof data === 'string' ? enc.encode(data) : data;
    return hex(await crypto.subtle.digest('SHA-256', bytes));
}
async function hmac(key, data) {
    const k = await crypto.subtle.importKey('raw', typeof key === 'string' ? enc.encode(key) : key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    return new Uint8Array(await crypto.subtle.sign('HMAC', k, enc.encode(data)));
}
/** RFC 3986 encoding as SigV4 requires (S3 object keys keep '/'). */
export function uriEncode(s, keepSlash = false) {
    let out = '';
    for (const ch of s) {
        if (/[A-Za-z0-9\-._~]/.test(ch) || (keepSlash && ch === '/')) {
            out += ch;
        }
        else {
            out += [...enc.encode(ch)]
                .map(b => `%${b.toString(16).toUpperCase().padStart(2, '0')}`)
                .join('');
        }
    }
    return out;
}
/** Returns the Authorization header value; `headers` must already contain host + x-amz-*. */
export async function signV4(creds, input) {
    const service = input.service ?? 's3';
    const date = input.amzDate.slice(0, 8);
    const lower = {};
    for (const [k, v] of Object.entries(input.headers)) {
        lower[k.toLowerCase()] = v.trim().replace(/\s+/g, ' ');
    }
    const names = Object.keys(lower).sort();
    const canonicalHeaders = names.map(n => `${n}:${lower[n]}\n`).join('');
    const signedHeaders = names.join(';');
    const query = [...input.url.searchParams.entries()]
        .map(([k, v]) => [uriEncode(k), uriEncode(v)])
        .sort(([a, av], [b, bv]) => (a === b ? (av < bv ? -1 : 1) : a < b ? -1 : 1))
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
    const path = uriEncode(decodeURIComponent(input.url.pathname), true);
    const canonicalRequest = [
        input.method,
        path,
        query,
        canonicalHeaders,
        signedHeaders,
        input.payloadHash,
    ].join('\n');
    const scope = `${date}/${input.region}/${service}/aws4_request`;
    const stringToSign = [
        'AWS4-HMAC-SHA256',
        input.amzDate,
        scope,
        await sha256Hex(canonicalRequest),
    ].join('\n');
    const kDate = await hmac(`AWS4${creds.secretAccessKey}`, date);
    const kRegion = await hmac(kDate, input.region);
    const kService = await hmac(kRegion, service);
    const kSigning = await hmac(kService, 'aws4_request');
    const signature = hex(await hmac(kSigning, stringToSign));
    return `AWS4-HMAC-SHA256 Credential=${creds.accessKeyId}/${scope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;
}
export class S3Error extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.status = status;
    }
}
export function objectUrl(t, key) {
    const base = new URL(t.endpoint);
    const encodedKey = uriEncode(key, true);
    if (t.pathStyle) {
        return new URL(`${base.origin}/${uriEncode(t.bucket)}/${encodedKey}`);
    }
    return new URL(`${base.protocol}//${t.bucket}.${base.host}/${encodedKey}`);
}
export function amzDate(now = new Date()) {
    return now
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '');
}
/**
 * Bounded so a stalled store cannot eat a cron run's subrequests: 3 tries = at most 2
 * extra subrequests per object, ~0.3–2.4 s of waiting.
 */
export const S3_RETRY_DEFAULTS = {
    maxAttempts: 3,
    baseDelayMs: 300,
    maxDelayMs: 2_000,
    sleep: ms => new Promise(r => setTimeout(r, ms)),
};
/** 5xx (S3's 503 SlowDown included) and 429 are transient; 4xx auth/validation errors are not. */
export function isRetryableStatus(status) {
    return status >= 500 || status === 429;
}
export function retryDelayMs(attempt, o, random = Math.random) {
    const ceiling = Math.min(o.maxDelayMs, o.baseDelayMs * 2 ** attempt);
    return Math.round(ceiling / 2 + (random() * ceiling) / 2);
}
export class S3Client {
    target;
    creds;
    fetchImpl;
    retry;
    constructor(target, creds, fetchImpl = globalFetch, retry = {}) {
        this.target = target;
        this.creds = creds;
        this.fetchImpl = fetchImpl;
        this.retry = { ...S3_RETRY_DEFAULTS, ...retry };
    }
    /**
     * Sends with retry on transient failures (5xx incl. SlowDown, 429, network errors).
     * Every try is re-signed so `x-amz-date` stays fresh. PUT/DELETE of a fixed key are
     * idempotent, so a retry after an ambiguous failure is safe.
     */
    async request(method, key, body, extra = {}) {
        for (let attempt = 0;; attempt++) {
            const last = attempt + 1 >= this.retry.maxAttempts;
            let resp;
            try {
                resp = await this.send(method, key, body, extra);
            }
            catch (err) {
                if (last) {
                    throw err;
                }
                await this.retry.sleep(retryDelayMs(attempt, this.retry));
                continue;
            }
            if (last || !isRetryableStatus(resp.status)) {
                return resp;
            }
            await resp.body?.cancel().catch(() => undefined);
            await this.retry.sleep(retryDelayMs(attempt, this.retry));
        }
    }
    async send(method, key, body, extra = {}) {
        const url = objectUrl(this.target, key);
        const payloadHash = await sha256Hex(body ?? new Uint8Array());
        const headers = {
            host: url.host,
            'x-amz-date': amzDate(),
            'x-amz-content-sha256': payloadHash,
            ...extra,
        };
        if (this.creds.sessionToken) {
            headers['x-amz-security-token'] = this.creds.sessionToken;
        }
        headers.authorization = await signV4(this.creds, {
            method,
            url,
            headers: Object.fromEntries(Object.entries(headers).filter(([k]) => k !== 'authorization')),
            payloadHash,
            region: this.target.region,
            amzDate: headers['x-amz-date'],
        });
        // `host` is set by fetch itself.
        const { host: _host, ...sendHeaders } = headers;
        return this.fetchImpl(url.toString(), {
            method,
            headers: sendHeaders,
            body: body && body.byteLength > 0 ? body : undefined,
            // Never follow a redirect to a host the endpoint check did not see.
            redirect: 'manual',
        });
    }
    async check(resp, what) {
        if (resp.ok) {
            return resp;
        }
        const text = (await resp.text().catch(() => '')).slice(0, 300);
        const code = /<Code>([^<]+)<\/Code>/.exec(text)?.[1];
        throw new S3Error(`${what} failed: HTTP ${resp.status}${code ? ` ${code}` : ''}`, resp.status);
    }
    async put(key, body, contentType, contentEncoding) {
        const extra = { 'content-type': contentType };
        if (contentEncoding) {
            extra['content-encoding'] = contentEncoding;
        }
        const resp = await this.check(await this.request('PUT', key, body, extra), `PUT ${key}`);
        return { etag: (resp.headers.get('etag') ?? '').replace(/"/g, '') };
    }
    async delete(key) {
        const resp = await this.request('DELETE', key);
        if (!resp.ok && resp.status !== 404) {
            await this.check(resp, `DELETE ${key}`);
        }
    }
    async getText(key) {
        const resp = await this.request('GET', key);
        if (resp.status === 404) {
            return null;
        }
        return (await this.check(resp, `GET ${key}`)).text();
    }
}
/** Sensible addressing default: virtual-hosted on AWS, path-style elsewhere (R2, MinIO, GCS). */
export function defaultPathStyle(endpoint) {
    try {
        return !new URL(endpoint).hostname.endsWith('amazonaws.com');
    }
    catch {
        return true;
    }
}
//# sourceMappingURL=s3.js.map